import React, { useMemo, useState } from "react";

export interface TableColumn<T> {
    /** Unique key for the column */
    key: string;
    /** Header label */
    header: string;
    /** How to render the cell */
    render: (row: T) => React.ReactNode;
    /** Plain-text value used for searching. If omitted, the column is not searchable. */
    searchValue?: (row: T) => string | null | undefined;
    width?: string;
}

interface SearchableTableProps<T> {
    title: string;
    count?: number;
    /**
     * "table" (default): normal table with column headers. Click a header to search that column.
     * "list": no column headers (cards/rows rendered by renderItem). Click the title to search.
     */
    variant?: "table" | "list";

    // ----- table variant -----
    columns?: TableColumn<T>[];

    // ----- list variant -----
    renderItem?: (row: T) => React.ReactNode;
    searchValue?: (row: T) => string | null | undefined;
    listItemClassName?: string;

    rows: T[];
    getRowKey: (row: T, index: number) => string;
    loading?: boolean;
    error?: unknown;
    loadingMessage?: string;
    errorMessage?: string;
    emptyMessage?: string;
    /** Shown when rows exist but none match the search */
    noMatchMessage?: string;
    /** Optional footer, e.g. <PaginationNew ... /> */
    footer?: React.ReactNode;

    cardClassName?: string;
    wrapperClassName?: string;
    footerClassName?: string;
    footerStyle?: React.CSSProperties;
}

/** Absolutely-positioned search box with the ✕ inside it. */
const SearchPopover = ({
    value,
    placeholder,
    onChange,
    onDismiss,
    onClear,
}: {
    value: string;
    placeholder: string;
    onChange: (v: string) => void;
    /** Called on blur / Enter: closes the box but keeps the typed text */
    onDismiss: () => void;
    /** Called by the ✕: clears the text and closes the box */
    onClear: () => void;
}) => (
    <div
        onClick={(e) => e.stopPropagation()}
        style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            width: "230px",
            background: "#fff",
            border: "1px solid var(--purple-line)",
            borderRadius: "8px",
            boxShadow: "0 6px 20px rgba(20, 24, 40, 0.12)",
            padding: "6px",
            textTransform: "none",
            letterSpacing: "normal",
            fontWeight: 500,
        }}
    >
        <div style={{ position: "relative" }}>
            <input
                autoFocus
                type="text"
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onDismiss}
                onKeyDown={(e) => {
                    if (e.key === "Escape") onClear();
                    if (e.key === "Enter") onDismiss();
                }}
                style={{
                    width: "100%",
                    padding: "7px 28px 7px 10px",
                    fontSize: "12.5px",
                    color: "var(--ink)",
                    background: "#fff",
                    border: "1px solid var(--line-2)",
                    borderRadius: "6px",
                    outline: "none",
                    boxSizing: "border-box",
                }}
            />
            <button
                type="button"
                aria-label="Clear search"
                // keep the input from blurring before the click registers
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClear}
                style={{
                    position: "absolute",
                    right: "6px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--ink-3)",
                    fontSize: "13px",
                    lineHeight: 1,
                    padding: "2px",
                }}
            >
                ✕
            </button>
        </div>
    </div>
);

function SearchableTable<T>({
    title,
    count,
    variant = "table",
    columns = [],
    renderItem,
    searchValue,
    listItemClassName = "grid gap-2 items-center p-4 border-[1px] border-solid border-surface-border agencyleft-card-item",
    rows,
    getRowKey,
    loading = false,
    error,
    loadingMessage = "Loading...",
    errorMessage = "Failed to load data. Please try again.",
    emptyMessage = "No data found.",
    noMatchMessage = "No results match your search.",
    footer,
    cardClassName = "",
    wrapperClassName = "responsive-table-wrapper",
    footerClassName = "",
    footerStyle,
}: SearchableTableProps<T>) {
    const isList = variant === "list";

    // Which search box is open ("__list__" for the list variant)
    const [openKey, setOpenKey] = useState<string | null>(null);
    const [filters, setFilters] = useState<Record<string, string>>({});

    const LIST_KEY = "__list__";
    const setFilter = (key: string, value: string) =>
        setFilters((prev) => ({ ...prev, [key]: value }));
    const clearAndClose = (key: string) => {
        setFilter(key, "");
        setOpenKey(null);
    };
    const dismiss = (key: string) =>
        setOpenKey((current) => (current === key ? null : current));

    const filteredRows = useMemo(() => {
        if (isList) {
            const q = (filters[LIST_KEY] ?? "").trim().toLowerCase();
            if (!q) return rows;
            return rows.filter((row) =>
                (searchValue?.(row) ?? "").toString().toLowerCase().includes(q),
            );
        }

        const active = columns.filter((c) => (filters[c.key] ?? "").trim() !== "");
        if (active.length === 0) return rows;

        return rows.filter((row) =>
            active.every((col) => {
                const text = (col.searchValue?.(row) ?? "").toString().toLowerCase();
                return text.includes(filters[col.key].trim().toLowerCase());
            }),
        );
    }, [rows, columns, filters, isList, searchValue]);

    const activeHeaderStyle = (key: string): React.CSSProperties =>
        (filters[key] ?? "").trim()
            ? { color: "var(--purple-deep)", textDecoration: "underline" }
            : {};

    const clickableLabelStyle: React.CSSProperties = {
        border: "none",
        background: "transparent",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        textTransform: "inherit",
        letterSpacing: "inherit",
        color: "inherit",
        textAlign: "left",
    };

    const messageBlock = (text: string) =>
        isList ? (
            <div className="p-4 text-sm text-gray-500">{text}</div>
        ) : (
            <tr>
                <td colSpan={columns.length || 1}>{text}</td>
            </tr>
        );

    const renderRows = () => {
        if (loading) return messageBlock(loadingMessage);
        if (error) return messageBlock(errorMessage);
        if (rows.length === 0) return messageBlock(emptyMessage);
        if (filteredRows.length === 0) return messageBlock(noMatchMessage);

        if (isList) {
            return filteredRows.map((row, index) => (
                <div key={getRowKey(row, index)} className={listItemClassName}>
                    {renderItem?.(row)}
                </div>
            ));
        }

        return filteredRows.map((row, index) => (
            <tr key={getRowKey(row, index)}>
                {columns.map((col) => (
                    <td key={col.key}>{col.render(row)}</td>
                ))}
            </tr>
        ));
    };

    return (
        <div className={`table-card relative ${cardClassName}`}>
            {/* Card header (title). In list mode, clicking the title opens the search box. */}
            <div className="table-header" style={{ position: "relative", zIndex: 2 }}>
                <h2 className="flex items-center" style={{ position: "relative" }}>
                    {isList && searchValue ? (
                        <button
                            type="button"
                            onClick={() => setOpenKey(LIST_KEY)}
                            title="Click to search"
                            style={{
                                ...clickableLabelStyle,
                                fontSize: "16px",
                                fontWeight: 600,
                                ...activeHeaderStyle(LIST_KEY),
                            }}
                        >
                            {title}
                        </button>
                    ) : (
                        title
                    )}
                    <span className="badge-count">{count ?? rows.length}</span>

                    {isList && openKey === LIST_KEY && (
                        <SearchPopover
                            value={filters[LIST_KEY] ?? ""}
                            placeholder={`Search ${title}`}
                            onChange={(v) => setFilter(LIST_KEY, v)}
                            onDismiss={() => dismiss(LIST_KEY)}
                            onClear={() => clearAndClose(LIST_KEY)}
                        />
                    )}
                </h2>
            </div>

            <div className={wrapperClassName}>
                {isList ? (
                    renderRows()
                ) : (
                    <table className="custom-table">
                        <thead style={{ position: "sticky", top: "-1px", zIndex: 3 }}>
                            <tr>
                                {columns.map((col) => {
                                    const searchable = !!col.searchValue;
                                    return (
                                        <th
                                            key={col.key}
                                            style={{ width: col.width, position: "relative" }}
                                        >
                                            {searchable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenKey(col.key)}
                                                    title={`Click to search ${col.header}`}
                                                    style={{
                                                        ...clickableLabelStyle,
                                                        ...activeHeaderStyle(col.key),
                                                    }}
                                                >
                                                    {col.header}
                                                </button>
                                            ) : (
                                                col.header
                                            )}

                                            {searchable && openKey === col.key && (
                                                <SearchPopover
                                                    value={filters[col.key] ?? ""}
                                                    placeholder={`Search ${col.header}`}
                                                    onChange={(v) => setFilter(col.key, v)}
                                                    onDismiss={() => dismiss(col.key)}
                                                    onClear={() => clearAndClose(col.key)}
                                                />
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>{renderRows()}</tbody>
                    </table>
                )}
            </div>

            {footer && (
                <div className={footerClassName} style={footerStyle}>
                    {footer}
                </div>
            )}
        </div>
    );
}

export default SearchableTable;